import React, { useEffect, useState } from 'react';

function useViewportWidth() {
  const [w, setW] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
}

function PortfolioHero({
  totalValue,
  holdingsValue,
  cashBalance,
  netContributions,
  realizedPnl,
  unrealizedPnl,
  totalPnl,
  dividendIncomeTtm,
  dividendIncomeAllTime,
  yieldOnCostTtm,
  dividendYieldTtm,
  annualDividendForecast,
  monthlyAvgIncomeTtm,
  xirr,
  reportCurrency,
  walletEnabled = true,
  visibleMetricIds = DEFAULT_METRIC_IDS,
}) {
  // When wallet is off, "Net Contributions" doesn't apply — it's shown as "Invested" instead.
  const allMetrics = [
    {
      id: 'totalValue',
      label: 'Total Value',
      value: formatCurrency(totalValue, reportCurrency),
      emphasis: true,
      tone: 'default',
    },
    {
      id: 'holdingsValue',
      label: 'Holdings Value',
      value: formatCurrency(holdingsValue, reportCurrency),
      tone: 'default',
    },
    walletEnabled && {
      id: 'cashBalance',
      label: 'Cash Balance',
      value: formatCurrency(cashBalance, reportCurrency),
      tone: 'default',
    },
    walletEnabled ? {
      id: 'netContributions',
      label: 'Net Contributions',
      value: formatCurrency(netContributions, reportCurrency),
      tone: 'default',
    } : {
      id: 'invested',
      label: 'Invested',
      value: formatCurrency(netContributions, reportCurrency),
      tone: 'default',
    },
    {
      id: 'realizedPnl',
      label: 'Realized P/L',
      value: formatSignedCurrency(realizedPnl, reportCurrency),
      tone: getTone(realizedPnl),
    },
    {
      id: 'unrealizedPnl',
      label: 'Unrealized P/L',
      value: formatSignedCurrency(unrealizedPnl, reportCurrency),
      tone: getTone(unrealizedPnl),
    },
    {
      id: 'totalPnl',
      label: 'Total P/L',
      value: formatSignedCurrency(totalPnl, reportCurrency),
      tone: getTone(totalPnl),
    },
    {
      id: 'dividendIncomeTtm',
      label: 'Dividend Income (TTM)',
      value: formatCurrency(dividendIncomeTtm, reportCurrency),
      tone: getTone(dividendIncomeTtm),
    },
    {
      id: 'yieldOnCostTtm',
      label: 'Yield on Cost (TTM)',
      value: formatPercent(yieldOnCostTtm),
      tone: getTone(yieldOnCostTtm),
    },
    {
      id: 'dividendYieldTtm',
      label: 'Dividend Yield (TTM)',
      value: formatPercent(dividendYieldTtm),
      tone: getTone(dividendYieldTtm),
    },
    {
      id: 'dividendIncomeAllTime',
      label: 'Dividend Income (All-time)',
      value: formatCurrency(dividendIncomeAllTime, reportCurrency),
      tone: getTone(dividendIncomeAllTime),
    },
    {
      id: 'annualDividendForecast',
      label: 'Annual Dividend Forecast',
      value: formatCurrency(annualDividendForecast, reportCurrency),
      tone: getTone(annualDividendForecast),
    },
    {
      id: 'monthlyAvgIncomeTtm',
      label: 'Avg Monthly Income (TTM)',
      value: formatCurrency(monthlyAvgIncomeTtm, reportCurrency),
      tone: getTone(monthlyAvgIncomeTtm),
    },
    {
      id: 'xirr',
      label: 'XIRR',
      value: xirr == null ? '—' : formatPercent(xirr * 100),
      tone: getTone(xirr),
    },
  ];

  const baseIds = Array.isArray(visibleMetricIds) && visibleMetricIds.length > 0
    ? visibleMetricIds
    : DEFAULT_METRIC_IDS;
  const visibleSet = new Set(baseIds);
  // Auto-migrate saved selection: if the wallet is off and the user previously had
  // 'netContributions' enabled, treat it as 'invested'.
  if (!walletEnabled && visibleSet.has('netContributions')) {
    visibleSet.add('invested');
  }

  const visibleMetrics = allMetrics
    .filter(Boolean)
    .filter((metric) => visibleSet.has(metric.id));

  const vw = useViewportWidth();
  // Responsive column count — guarantees 2 cols on phones (avoids the wobble
  // with CSS-only minmax + auto-fit).
  const columns = vw < 480 ? 'repeat(2, 1fr)'
                : vw < 768 ? 'repeat(auto-fit, minmax(150px, 1fr))'
                : 'repeat(auto-fit, minmax(min(210px, 100%), 1fr))';
  const responsiveGrid = { ...gridStyle, gridTemplateColumns: columns };

  return (
    <section className="rp-hero-grid" style={responsiveGrid}>
      {visibleMetrics.map((metric) => (
        <MetricCard
          key={metric.id}
          label={metric.label}
          value={metric.value}
          tone={metric.tone}
          emphasis={metric.emphasis}
        />
      ))}
    </section>
  );
}

export const PORTFOLIO_HERO_METRICS = [
  { id: 'totalValue', label: 'Total Value' },
  { id: 'holdingsValue', label: 'Holdings Value' },
  { id: 'cashBalance', label: 'Cash Balance', walletOnly: true },
  { id: 'netContributions', label: 'Net Contributions', walletOnly: true },
  { id: 'invested', label: 'Invested', walletOff: true },
  { id: 'realizedPnl', label: 'Realized P/L' },
  { id: 'unrealizedPnl', label: 'Unrealized P/L' },
  { id: 'totalPnl', label: 'Total P/L' },
  { id: 'dividendIncomeTtm', label: 'Dividend Income (TTM)' },
  { id: 'yieldOnCostTtm', label: 'Yield on Cost (TTM)' },
  { id: 'dividendYieldTtm', label: 'Dividend Yield (TTM)' },
  { id: 'dividendIncomeAllTime', label: 'Dividend Income (All-time)' },
  { id: 'annualDividendForecast', label: 'Annual Dividend Forecast' },
  { id: 'monthlyAvgIncomeTtm', label: 'Avg Monthly Income (TTM)' },
  { id: 'xirr', label: 'XIRR' },
];

// Helper for the settings UI — returns only metrics that apply to the current wallet state.
export function getAvailableHeroMetrics(walletEnabled) {
  return PORTFOLIO_HERO_METRICS.filter((m) => {
    if (m.walletOnly) return walletEnabled;
    if (m.walletOff)  return !walletEnabled;
    return true;
  });
}

export const DEFAULT_METRIC_IDS = [
  'totalValue',
  'holdingsValue',
  'cashBalance',
  'netContributions',
  'realizedPnl',
  'unrealizedPnl',
  'totalPnl',
];

function MetricCard({ label, value, tone = 'default', emphasis = false }) {
  return (
    <div style={cardStyle}>
      <div style={labelStyle}>{label}</div>
      <div
        className={emphasis ? 'rp-hero-value rp-hero-value-emphasis' : 'rp-hero-value'}
        style={{
          ...valueStyle,
          ...(emphasis ? emphasisValueStyle : {}),
          color: getColorForTone(tone),
        }}
      >
        {value}
      </div>
    </div>
  );
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

function formatSignedCurrency(value, currency) {
  const numberValue = Number(value || 0);
  const formatted = formatCurrency(Math.abs(numberValue), currency);

  if (numberValue > 0) return '+' + formatted;
  if (numberValue < 0) return '-' + formatted;

  return formatted;
}

function formatPercent(value) {
  const numeric = Number(value || 0);

  return (
    new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric) + '%'
  );
}

function getTone(value) {
  const numeric = Number(value || 0);

  if (numeric > 0) return 'positive';
  if (numeric < 0) return 'negative';
  return 'default';
}

function getColorForTone(tone) {
  if (tone === 'positive') return 'var(--success)';
  if (tone === 'negative') return 'var(--danger)';
  return 'var(--text-primary)';
}

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(210px, 100%), 1fr))',
  gap: '16px',
  marginBottom: '24px',
  minWidth: 0,
};

const cardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '20px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
};

const labelStyle = {
  color: 'var(--text-secondary)',
  fontSize: '14px',
  marginBottom: '10px',
  fontWeight: 700,
};

const valueStyle = {
  color: 'var(--text-primary)',
  fontSize: '24px',
  fontWeight: 800,
};

const emphasisValueStyle = {
  fontSize: '30px',
};

export default PortfolioHero;